import { describeError, useApi } from '@classgo/app-kit';
import type { RoleTemplateView, SchoolUserView, UserAccessView } from '@classgo/shared';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  Modal,
  Table,
  useToast,
} from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';

import type { messages } from '../../i18n';
import { useT } from '../../i18n';
import { useTenant } from '../../tenant/tenant-provider';
import { usersApi, usersQueries } from '../../users/api';
import { PermissionPicker } from '../roles/permission-picker';
import type { PermissionSource } from '../roles/types';
import { InvitationStatus } from '../student-detail';

type Key = keyof typeof messages;

const fullName = (user: SchoolUserView) => `${user.givenName} ${user.familyName}`.trim();

/**
 * The people who administer the school (#130): who they are, what templates they hold, whether
 * they can sign in, and, per person, where each permission comes from. Teachers and guardians
 * are invited from their own screens; this one is for the staff.
 */
export const UsersScreen = () => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const { session } = useTenant();
  const can = (key: string) => session.permissions.all || session.permissions.keys.includes(key);
  const users = useQuery(usersQueries.users(api));
  const roles = useQuery(usersQueries.roles(api));
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<SchoolUserView | null>(null);
  const refresh = () => queries.invalidateQueries({ queryKey: ['users'] });

  const needle = search.trim().toLowerCase();
  const rows = (users.data ?? []).filter(
    (user) =>
      needle === '' ||
      fullName(user).toLowerCase().includes(needle) ||
      (user.email ?? '').toLowerCase().includes(needle),
  );
  const statusTone = (status: string) =>
    status === 'active'
      ? 'success'
      : status === 'invited'
        ? 'info'
        : status === 'disabled'
          ? 'danger'
          : 'gray';

  return (
    <>
      <Card
        title={t('users.title')}
        flush
        actions={
          can('users:write') ? (
            <Button onClick={() => setCreating(true)}>{t('users.new')}</Button>
          ) : undefined
        }
      >
        <div className="space-y-3 px-4 pt-4 md:px-6">
          <p className="text-sm text-gray-600">{t('users.intro')}</p>
          <div className="max-w-md">
            <Input
              label={t('students.filter.search')}
              help={t('users.search.help')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>
        <div className="mt-3">
          <Table<SchoolUserView>
            caption={t('users.caption')}
            loading={users.isPending}
            rows={rows}
            rowKey={(user) => user.id}
            onRowClick={(user) => setSelected(user)}
            empty={
              <EmptyState title={t('users.empty.title')} description={t('users.empty.body')} />
            }
            columns={[
              {
                key: 'person',
                header: t('users.column.person'),
                sortable: true,
                sortValue: (user) => `${user.familyName} ${user.givenName}`,
                cell: (user) => (
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">{fullName(user)}</p>
                    <p className="truncate text-xs text-gray-500">{user.email}</p>
                  </div>
                ),
              },
              {
                key: 'role',
                header: t('users.column.role'),
                cell: (user) => (
                  <Badge tone={user.roles.includes('SCHOOL_OWNER') ? 'brand' : 'gray'}>
                    {t(
                      `users.role.${user.roles.includes('SCHOOL_OWNER') ? 'SCHOOL_OWNER' : 'SCHOOL_STAFF'}` as Key,
                    )}
                  </Badge>
                ),
              },
              {
                key: 'templates',
                header: t('users.column.templates'),
                cell: (user) =>
                  user.roles.includes('SCHOOL_OWNER') ? (
                    <span className="text-xs text-gray-500">{t('account.permissions.all')}</span>
                  ) : user.templates.length === 0 ? (
                    <span className="text-xs text-gray-500">{t('users.templates.none')}</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {user.templates.map((template) => (
                        <Badge key={template.id} tone="info">
                          {template.name}
                        </Badge>
                      ))}
                    </div>
                  ),
              },
              {
                key: 'access',
                header: t('users.column.access'),
                cell: (user) => (
                  <Badge tone={statusTone(user.status)}>
                    {t(`users.status.${user.status}` as Key)}
                  </Badge>
                ),
              },
              {
                key: 'overrides',
                header: t('users.column.overrides'),
                cell: (user) =>
                  user.overridesCount > 0 ? (
                    <span className="text-sm text-gray-700">
                      {t('users.overrides.count', { count: user.overridesCount })}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">{t('common.none')}</span>
                  ),
              },
            ]}
          />
        </div>
      </Card>

      {creating ? (
        <CreateUserDialog
          templates={roles.data ?? []}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void refresh();
          }}
        />
      ) : null}

      {selected ? (
        <UserDetailDialog
          user={selected}
          templates={roles.data ?? []}
          canEdit={can('users:write')}
          canAssign={can('permissions:write')}
          onClose={() => setSelected(null)}
          onChanged={() => void refresh()}
        />
      ) : null}
    </>
  );
};

const CreateUserDialog = ({
  templates,
  onClose,
  onCreated,
}: {
  templates: RoleTemplateView[];
  onClose: () => void;
  onCreated: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [email, setEmail] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      usersApi(api).createUser({
        givenName: givenName.trim(),
        familyName: familyName.trim(),
        email: email.trim(),
        templateIds: [...picked],
        invite: true,
      }),
    onSuccess: () => {
      toast.show({ tone: 'success', title: t('users.created') });
      onCreated();
    },
    onError: (thrown) => setError(describeError(t, thrown)),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    create.mutate();
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={t('users.new')}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="user-form" loading={create.isPending}>
            {t('users.create.submit')}
          </Button>
        </>
      }
    >
      <form id="user-form" onSubmit={submit} className="space-y-4">
        {error ? (
          <p role="alert" className="text-sm text-danger-700">
            {error}
          </p>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('users.field.givenName')}
            required
            value={givenName}
            onChange={(event) => setGivenName(event.target.value)}
          />
          <Input
            label={t('users.field.familyName')}
            required
            value={familyName}
            onChange={(event) => setFamilyName(event.target.value)}
          />
        </div>
        <Input
          label={t('users.field.email')}
          help={t('users.field.email.help')}
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <fieldset>
          <legend className="text-sm font-medium text-gray-900">
            {t('users.field.templates')}
          </legend>
          <p className="mb-2 text-xs text-gray-500">{t('users.field.templates.help')}</p>
          <div className="space-y-1">
            {templates.map((template) => (
              <Checkbox
                key={template.id}
                label={template.name}
                description={
                  template.description ??
                  t('roles.permissions.count', { count: template.permissionKeys.length })
                }
                checked={picked.has(template.id)}
                onChange={(event) => {
                  const next = new Set(picked);
                  if (event.target.checked) next.add(template.id);
                  else next.delete(template.id);
                  setPicked(next);
                }}
              />
            ))}
          </div>
        </fieldset>
      </form>
    </Modal>
  );
};

const UserDetailDialog = ({
  user,
  templates,
  canEdit,
  canAssign,
  onClose,
  onChanged,
}: {
  user: SchoolUserView;
  templates: RoleTemplateView[];
  canEdit: boolean;
  canAssign: boolean;
  onClose: () => void;
  onChanged: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queries = useQueryClient();
  const access = useQuery(usersQueries.access(api, user.id));
  const catalog = useQuery(usersQueries.permissions(api));
  const owner = user.roles.includes('SCHOOL_OWNER');
  const [picked, setPicked] = useState<Set<string>>(new Set(user.templates.map((row) => row.id)));
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map());
  const [renaming, setRenaming] = useState(false);
  const [givenName, setGivenName] = useState(user.givenName);
  const [familyName, setFamilyName] = useState(user.familyName);
  const [confirmDisable, setConfirmDisable] = useState(false);
  useEffect(() => {
    if (access.data) {
      setOverrides(new Map(access.data.overrides.map((row) => [row.permissionKey, row.granted])));
    }
  }, [access.data]);
  const done = (title: Key) => {
    toast.show({ tone: 'success', title: t(title) });
    void queries.invalidateQueries({ queryKey: ['users', 'access', user.id] });
    onChanged();
  };
  const fail = (thrown: unknown) =>
    toast.show({ tone: 'error', title: t('users.title'), description: describeError(t, thrown) });
  const saveTemplates = useMutation({
    mutationFn: () => usersApi(api).setTemplates(user.id, { templateIds: [...picked] }),
    onSuccess: () => done('users.detail.templates.saved'),
    onError: fail,
  });
  const saveOverrides = useMutation({
    mutationFn: () =>
      usersApi(api).setOverrides(user.id, {
        overrides: [...overrides.entries()].map(([permissionKey, granted]) => ({
          permissionKey,
          granted,
        })),
      }),
    onSuccess: () => done('users.detail.overrides.saved'),
    onError: fail,
  });
  const update = useMutation({
    mutationFn: (body: {
      givenName?: string;
      familyName?: string;
      status?: 'active' | 'disabled';
    }) => usersApi(api).updateUser(user.id, body),
    onSuccess: () => {
      setRenaming(false);
      setConfirmDisable(false);
      done('users.updated');
      onClose();
    },
    onError: fail,
  });

  // From the templates the person holds, what each permission's source is once overrides apply.
  const fromTemplates = new Set(
    (access.data?.templates ?? []).flatMap((template) => template.permissionKeys),
  );
  const sources = new Map<string, PermissionSource>();
  const effective = new Set<string>();
  for (const item of catalog.data ?? []) {
    const override = overrides.get(item.key);
    const granted = override ?? fromTemplates.has(item.key);
    if (granted) effective.add(item.key);
    sources.set(
      item.key,
      override === true
        ? 'added'
        : override === false
          ? 'removed'
          : fromTemplates.has(item.key)
            ? 'template'
            : 'none',
    );
  }
  const toggle = (key: string, checked: boolean) => {
    const next = new Map(overrides);
    // Checking a key the templates already give (or unchecking one they do not) is no exception.
    if (checked === fromTemplates.has(key)) next.delete(key);
    else next.set(key, checked);
    setOverrides(next);
  };
  const effectiveView: UserAccessView['effective'] = owner
    ? { all: true, keys: [] }
    : { all: false, keys: [...effective].sort() };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('users.detail.title', { name: fullName(user) })}
      {...(user.email ? { description: user.email } : {})}
      size="lg"
      actions={
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      }
    >
      <div className="space-y-5">
        <section className="flex flex-wrap items-center gap-3">
          <Badge tone={owner ? 'brand' : 'gray'}>
            {t(`users.role.${owner ? 'SCHOOL_OWNER' : 'SCHOOL_STAFF'}` as Key)}
          </Badge>
          <InvitationStatus userId={user.id} email={user.email} />
          {canEdit ? (
            <div className="ml-auto flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" onClick={() => setRenaming(true)}>
                {t('users.detail.rename')}
              </Button>
              {user.status === 'disabled' ? (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={update.isPending}
                  onClick={() => update.mutate({ status: 'active' })}
                >
                  {t('users.detail.enable')}
                </Button>
              ) : (
                <Button size="sm" variant="danger" onClick={() => setConfirmDisable(true)}>
                  {t('users.detail.disable')}
                </Button>
              )}
            </div>
          ) : null}
        </section>

        {renaming ? (
          <form
            className="grid gap-3 rounded-card border border-gray-200 p-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
            onSubmit={(event) => {
              event.preventDefault();
              update.mutate({ givenName: givenName.trim(), familyName: familyName.trim() });
            }}
          >
            <Input
              label={t('users.field.givenName')}
              required
              value={givenName}
              onChange={(event) => setGivenName(event.target.value)}
            />
            <Input
              label={t('users.field.familyName')}
              required
              value={familyName}
              onChange={(event) => setFamilyName(event.target.value)}
            />
            <Button type="submit" loading={update.isPending}>
              {t('common.save')}
            </Button>
          </form>
        ) : null}

        {confirmDisable ? (
          <div className="space-y-2 rounded-card border border-danger-600/30 bg-danger-100 p-3 text-sm text-danger-700">
            <p>{t('users.detail.disable.body', { name: fullName(user) })}</p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="danger"
                loading={update.isPending}
                onClick={() => update.mutate({ status: 'disabled' })}
              >
                {t('users.detail.disable')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setConfirmDisable(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : null}

        {owner ? (
          <p className="text-sm text-gray-600">{t('users.detail.effective.all')}</p>
        ) : (
          <>
            <section>
              <h3 className="text-sm font-semibold text-gray-900">{t('users.detail.templates')}</h3>
              <div className="mt-2 space-y-1">
                {templates.map((template) => (
                  <Checkbox
                    key={template.id}
                    label={template.name}
                    description={t('roles.permissions.count', {
                      count: template.permissionKeys.length,
                    })}
                    checked={picked.has(template.id)}
                    disabled={!canAssign}
                    onChange={(event) => {
                      const next = new Set(picked);
                      if (event.target.checked) next.add(template.id);
                      else next.delete(template.id);
                      setPicked(next);
                    }}
                  />
                ))}
              </div>
              {canAssign ? (
                <Button
                  className="mt-2"
                  size="sm"
                  loading={saveTemplates.isPending}
                  onClick={() => saveTemplates.mutate()}
                >
                  {t('users.detail.templates.save')}
                </Button>
              ) : null}
            </section>

            <section>
              <h3 className="text-sm font-semibold text-gray-900">{t('users.detail.overrides')}</h3>
              <p className="mb-2 text-xs text-gray-500">{t('users.detail.overrides.help')}</p>
              <PermissionPicker
                catalog={catalog.data ?? []}
                selected={effective}
                sources={sources}
                disabled={!canAssign}
                onToggle={toggle}
              />
              {canAssign ? (
                <Button
                  className="mt-2"
                  size="sm"
                  loading={saveOverrides.isPending}
                  onClick={() => saveOverrides.mutate()}
                >
                  {t('users.detail.overrides.save')}
                </Button>
              ) : null}
            </section>

            <section>
              <h3 className="text-sm font-semibold text-gray-900">
                {t('users.detail.effective')} ·{' '}
                {t('users.detail.effective.count', { count: effectiveView.keys.length })}
              </h3>
              <p className="mt-1 text-xs text-gray-500">{effectiveView.keys.join(' · ')}</p>
            </section>
          </>
        )}
      </div>
    </Modal>
  );
};
